import React, { memo } from 'react';

/**
 * PaginatorFooter - Componente de presentación puro ("tonto") y memoizado.
 * Resuelve la navegación de páginas de forma unificada para todos los tableros.
 * @param {number} currentPage - Índice de la página actual.
 * @param {number} totalPages - Cantidad total de páginas calculadas.
 * @param {number} currentLength - Cantidad de registros visibles en la página actual.
 * @param {number} totalLength - Cantidad total de registros filtrados.
 * @param {function} onPageChange - Callback que despacha el cambio de página (ej: setCurrentPage).
 * @param {string} label - Etiqueta descriptiva del tipo de registros (ej: "morosos", "casos", "gestiones").
 * @param {string} accentClass - Clases Tailwind de personalización para el badge de página actual.
 */
const PaginatorFooter = memo(({ 
    currentPage, 
    totalPages, 
    currentLength, 
    totalLength, 
    onPageChange, 
    label = "resultados", 
    accentClass = "text-indigo-900 bg-indigo-50 border-indigo-100" 
}) => {
    
    // Handlers seguros para evitar desbordamiento de páginas
    const handlePrev = () => {
        onPageChange(prev => Math.max(1, prev - 1));
    };

    const handleNext = () => {
        onPageChange(prev => Math.min(totalPages, prev + 1));
    };

    return (
        <footer className="h-14 shrink-0 bg-slate-50 border-t border-slate-200 flex justify-between items-center px-8 text-sm text-left select-none">
            {/* Contador de registros en el extremo izquierdo */}
            <p className="text-slate-500 font-medium">
                Mostrando <span className="font-bold text-slate-800">{currentLength}</span> de <span className="font-bold text-slate-800">{totalLength}</span> {label}
            </p>
            
            {/* Botonera de control en el extremo derecho */}
            <div className="flex gap-2">
                {/* Botón Anterior */}
                <button 
                    type="button" 
                    onClick={handlePrev} 
                    disabled={currentPage === 1} 
                    className="px-4 py-2 bg-white border border-slate-300 rounded-lg font-bold text-slate-600 hover:bg-slate-50 shadow-sm active:scale-95 transition-all text-sm disabled:opacity-30 disabled:cursor-not-allowed select-none"
                >
                    Anterior
                </button>
                
                {/* Badge de Página Actual (Estilizado dinámicamente) */}
                <div className={`flex items-center px-4 font-black rounded-lg border tabular-nums text-sm ${accentClass}`}>
                    Página {currentPage} de {totalPages || 1}
                </div>
                
                {/* Botón Siguiente */}
                <button 
                    type="button" 
                    onClick={handleNext} 
                    disabled={currentPage >= totalPages || totalPages === 0} 
                    className="px-4 py-2 bg-white border border-slate-300 rounded-lg font-bold text-slate-600 hover:bg-slate-50 shadow-sm active:scale-95 transition-all text-sm disabled:opacity-30 disabled:cursor-not-allowed select-none"
                >
                    Siguiente
                </button>
            </div>
        </footer>
    );
});

PaginatorFooter.displayName = 'PaginatorFooter';

export default PaginatorFooter;